import { computeRegionBounds, type Region } from '../utils/geometry';
import type { Course } from '../course/Course';
import type { SplatMapBounds } from './SplatMapBaker';

/**
 * Scans every hole's fairway/green/bunker/out-of-bounds regions and tee/pin
 * positions to find the world-space extent the terrain needs to cover.
 * This is what makes the terrain mesh's size and position *derived from
 * Course data* rather than an arbitrary hardcoded box — a course with
 * different hole layouts gets correctly sized/positioned terrain for free.
 */
export function computeCourseBounds(course: Course, margin = 40): SplatMapBounds {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;

  const consider = (x: number, z: number): void => {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  };

  const considerRegion = (region: Region): void => {
    const bounds = computeRegionBounds(region);
    consider(bounds.centerX - bounds.width / 2, bounds.centerZ - bounds.depth / 2);
    consider(bounds.centerX + bounds.width / 2, bounds.centerZ + bounds.depth / 2);
  };

  for (const hole of course.holes) {
    for (const tee of hole.def.teeBoxes) consider(tee.position.x, tee.position.z);
    for (const pin of hole.def.pins) consider(pin.position.x, pin.position.z);
    considerRegion(hole.def.fairway);
    considerRegion(hole.def.green);
    hole.def.bunkers.forEach(considerRegion);
    hole.def.rough.forEach(considerRegion);
    hole.def.outOfBounds.forEach(considerRegion);
  }

  if (!Number.isFinite(minX)) {
    // No holes yet (shouldn't happen in practice) — fall back to a small default box.
    return { minX: -50, maxX: 50, minZ: -50, maxZ: 50 };
  }

  return { minX: minX - margin, maxX: maxX + margin, minZ: minZ - margin, maxZ: maxZ + margin };
}
